import { create, StateCreator } from 'zustand';
import {
	DeviceWithCamerasDTO,
	DeployActionEnum,
	DeploymentDTO,
	DeviceStatusEnum,
	SoftwareNameEnum,
} from 'api/api_code';

import {
	persist,
	createJSONStorage,
	devtools,
	PersistOptions,
} from 'zustand/middleware';
import { CameraLicense } from 'types/global';
import { getCameraLicense } from '../components/camera/helpers/camera';
import { immer } from 'zustand/middleware/immer';
import { SERVERLESS_CLOUD } from '../constants/cameras';

type CameraAction = {
	_reset: () => void;
	getLicenseName: () => string;
	hasExternalCamera: () => boolean;
	isBusy: () => boolean;
	isReady: () => boolean;
	isServerless: () => boolean;
	isDisconnected: () => boolean;
};
type CameraConfigState = {
	cameraConfig: DeviceWithCamerasDTO;
	cameraConfigBE: DeviceWithCamerasDTO;
	externalCameraUuid: string | null;
};
const cameraInitialState: DeviceWithCamerasDTO = {
	uuid: '',
	name: '',
	software_installed: [],
	software_installing: [],
	status: 'offline',
	producer: {
		serial_number: '',
		license_ticket_id: '',
		license_type: '',
		hostname: '',
		server_model: { name: '' },
	},
	fields: [{ uuid: '', name: '', timezone: '' }],
	framerate: null,
	lens_size: null,
	organization: { name: '' },
};
const initialState: CameraConfigState = {
	cameraConfig: cameraInitialState,
	cameraConfigBE: cameraInitialState,
	externalCameraUuid: null,
};
const cameraPersistOpts: PersistOptions<CameraConfigState & CameraAction> = {
	name: 'camera-store',
	storage: createJSONStorage(() => sessionStorage),
};
const injectMiddlewares = (
	f: StateCreator<
		CameraConfigState & CameraAction,
		[
			['zustand/devtools', never],
			['zustand/persist', CameraConfigState & CameraAction],
			['zustand/immer', never],
		]
	>
) => devtools(persist(immer(f), cameraPersistOpts), { name: 'CameraStore' });

const useCameraStore = create<CameraConfigState & CameraAction>()(
	injectMiddlewares((set, get) => ({
		...initialState,
		_reset: () => set(initialState, false, 'camera/_reset'),
		getLicenseName: () =>
			getCameraLicense(get().cameraConfig?.producer?.license_type),
		hasExternalCamera: () => !!get().externalCameraUuid || false,
		isBusy: () => {
			const cameraStatus = getCurrentCameraStatus(
				get().cameraConfig.status,
				softwareStatusSelector(get())
			);
			return (
				(!cameraStatus.isReady && !cameraStatus.isNotConfigured) ||
				cameraStatus.isUpgrading ||
				cameraStatus.isInstalling ||
				cameraStatus.isRecording
			);
		},
		isReady: () => get().cameraConfig.status === DeviceStatusEnum.Ready,
		isServerless: () =>
			get().cameraConfig?.producer?.server_model?.name === SERVERLESS_CLOUD,
		isDisconnected: () =>
			get().cameraConfig.status === DeviceStatusEnum.CameraUnavailable,
	}))
);

const softwareStatusSelector = (state: CameraConfigState) =>
	state.cameraConfigBE.software_installing?.find(
		(item) => item.version?.software?.name === SoftwareNameEnum.Automatictv
	)?.action;

const softwareVersionSelector = (state: CameraConfigState) =>
	state.cameraConfig.software_installed?.find(
		(item) => item.version?.software?.name === SoftwareNameEnum.Automatictv
	)?.version?.version_number;

const isProBroadcastSelector = (state: CameraConfigState) => {
	const license = state?.cameraConfig?.producer?.license_type;
	return (
		license !== CameraLicense.ATV_ACE &&
		license !== CameraLicense.ATV_ONE &&
		license !== ''
	);
};

const isOneSelector = (state: CameraConfigState) => {
	const license = state.cameraConfig?.producer?.license_type;
	return license === CameraLicense.ATV_ONE;
};

const getCurrentCameraStatus = (
	cameraStatus: DeviceWithCamerasDTO['status'],
	softwareStatus: DeploymentDTO['action']
) => {
	return {
		isReady: cameraStatus === DeviceStatusEnum.Ready,
		isOffline: cameraStatus === DeviceStatusEnum.Offline,
		isNotConfigured: cameraStatus === DeviceStatusEnum.NotConfigured,
		isUpgrading: softwareStatus === DeployActionEnum.Upgrade,
		isInstalling: softwareStatus === DeployActionEnum.Install,
		isRecording: cameraStatus === DeviceStatusEnum.Recording,
		isDisconnected: cameraStatus === DeviceStatusEnum.CameraUnavailable,
	};
};
const cameraConfigSelector = (state: CameraConfigState) => state.cameraConfig;
const cameraConfigBESelector = (state: CameraConfigState) =>
	state.cameraConfigBE;
const externalCameraUuidSelector = (state: CameraConfigState) =>
	state.externalCameraUuid;
const licenseNameSelector = (state: CameraConfigState) =>
	getCameraLicense(state.cameraConfig?.producer?.license_type);
const isServerlessSelector = (state: CameraConfigState) =>
	state.cameraConfig?.producer?.server_model?.name === SERVERLESS_CLOUD;
const isBusySelector = (state: CameraConfigState & CameraAction) => {
	const cameraStatus = getCurrentCameraStatus(
		state.cameraConfig.status,
		softwareStatusSelector(state)
	);
	return (
		(!cameraStatus.isReady && !cameraStatus.isNotConfigured) ||
		cameraStatus.isUpgrading ||
		cameraStatus.isInstalling ||
		cameraStatus.isRecording
	);
};
const isReadySelector = (state: CameraConfigState) =>
	state.cameraConfig.status === DeviceStatusEnum.Ready;
const isDisconnectedSelector = (state: CameraConfigState) =>
	state.cameraConfig.status === DeviceStatusEnum.CameraUnavailable;
export {
	useCameraStore,
	isProBroadcastSelector,
	isOneSelector,
	softwareStatusSelector,
	softwareVersionSelector,
	cameraConfigSelector,
	cameraConfigBESelector,
	licenseNameSelector,
	isServerlessSelector,
	isBusySelector,
	isReadySelector,
	isDisconnectedSelector,
	externalCameraUuidSelector,
};
